/**
 * Mobile Petty Cash — My Expenses list.
 * Shows the current user's submitted requests with status filter chips.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ListChecks, MapPin, PlusCircle, X } from 'lucide-react';
import { api, apiGet } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_CLASSES = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
  'Under Review': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Returned for Correction': 'bg-amber-50 text-amber-700 border-amber-200',
};

// Item 1 — Self-Edit Window: editable until an approver acts OR 2 hours
// pass since submission, whichever is first. "Returned for Correction" has
// no time limit since an approver explicitly invited the edit.
const SELF_EDIT_WINDOW_MS = 2 * 60 * 60 * 1000;
function canEdit(r) {
  if (r.status === 'Returned for Correction') return true;
  if (r.status === 'pending') {
    const created = new Date(r.created_at).getTime();
    return Number.isFinite(created) && (Date.now() - created) <= SELF_EDIT_WINDOW_MS;
  }
  return false;
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest font-display text-slate-500 px-0.5">{label}</label>
    {children}
  </div>
);

function EditExpenseSheet({ item, categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    actual_amount: item.actual_amount || 0,
    category: item.category || '',
    vendor_name: item.vendor_name || '',
    invoice_number: item.invoice_number || '',
  });
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const isReturned = item.status === 'Returned for Correction';

  const onPickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (f) setPhoto(f);
    e.target.value = '';
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, actual_amount: Number(form.actual_amount || 0) };
      if (photo) {
        body.bill_image_base64 = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result || '').split(',')[1] || '');
          r.onerror = reject;
          r.readAsDataURL(photo);
        });
        body.bill_image_mime = photo.type || 'image/jpeg';
      }
      await api.put(`/petty-cash/requests/${item.id}/resubmit`, body);
      toast.success('Expense updated and resubmitted for approval');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end" onClick={onClose} data-testid="m-pc-edit-sheet">
      <div className="bg-white w-full rounded-t-2xl p-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-bold text-sm text-[#3b4872]">Edit Expense</div>
          <button onClick={onClose} data-testid="m-pc-edit-close"><X size={18} /></button>
        </div>
        {isReturned && item.return_remarks && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-2 text-[11px] text-amber-800 mb-3">
            <b>Returned for correction:</b> {item.return_remarks}
          </div>
        )}
        <div className="space-y-2.5">
          <Field label="Vendor">
            <input value={form.vendor_name} onChange={(e) => setForm(f => ({ ...f, vendor_name: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="m-pc-edit-vendor" />
          </Field>
          <Field label="Category">
            <select value={form.category} onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" data-testid="m-pc-edit-category">
              <option value={form.category}>{form.category}</option>
              {categories.filter(c => c.name !== form.category).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Actual Amount ₹">
            <input type="number" inputMode="decimal" value={form.actual_amount} onChange={(e) => setForm(f => ({ ...f, actual_amount: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="m-pc-edit-amount" />
          </Field>
          <Field label="Invoice Number">
            <input value={form.invoice_number} onChange={(e) => setForm(f => ({ ...f, invoice_number: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="m-pc-edit-invoice" />
          </Field>
          <Field label="Replace Bill Photo (optional)">
            <input ref={fileRef} hidden type="file" accept="image/*" capture="environment" onChange={onPickPhoto} />
            <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-slate-300 rounded-sm py-2 text-xs text-slate-500" data-testid="m-pc-edit-photo-btn">
              {photo ? `Picked: ${photo.name}` : 'Capture new bill photo'}
            </button>
          </Field>
        </div>
        <button onClick={save} disabled={saving} className="w-full mt-4 bg-[#3b4872] text-white font-display font-bold rounded-sm py-2.5 disabled:opacity-50" data-testid="m-pc-edit-save">
          {saving ? 'Saving…' : 'Save & Resubmit'}
        </button>
      </div>
    </div>
  );
}

export default function MobilePettyCashList() {
  const { user } = useAuth() || {};
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => {
    if (!user) return;
    setLoading(true);
    const submittedBy = user.id || user.email;
    const params = { submitted_by: submittedBy, limit: 200 };
    if (filter !== 'all') params.status = filter;
    apiGet('/petty-cash/requests', params)
      .then((d) => setItems(d?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user, filter]);
  useEffect(() => {
    apiGet('/petty-cash/categories').then((d) => setCategories(d?.items || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 pb-6" data-testid="m-petty-cash-list">
      <div className="bg-[#3b4872] text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/m/petty-cash" className="p-1.5 -ml-1.5"><ArrowLeft size={20} /></Link>
        <div className="flex-1">
          <div className="font-display font-bold flex items-center gap-1.5"><ListChecks size={14} /> My Expenses</div>
          <div className="text-[10px] text-slate-300">Status &amp; history</div>
        </div>
        <Link to="/m/petty-cash/expense" className="p-1.5 -mr-1.5" data-testid="m-pc-list-new"><PlusCircle size={20} /></Link>
      </div>

      <div className="px-4 pt-3 flex gap-1.5 overflow-x-auto" data-testid="m-pc-list-filter">
        {['all', 'pending', 'approved', 'rejected'].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`m-pc-filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-[11px] font-display font-bold uppercase tracking-widest whitespace-nowrap border ${
              filter === s ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-300'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="p-4 space-y-2">
        {loading ? (
          <div className="text-center text-slate-500 text-sm py-6">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-slate-500 text-sm py-12">
            No expenses yet.<br />
            <Link to="/m/petty-cash/expense" className="text-indigo-600 underline mt-2 inline-block">Submit your first expense</Link>
          </div>
        ) : items.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`m-pc-row-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-sm text-slate-900 truncate">{r.vendor_name || '—'}</div>
                <div className="text-[11px] text-slate-500 truncate">{r.project_name || '—'} · {r.category}</div>
              </div>
              <span className={`px-2 py-0.5 rounded border text-[10px] font-display font-bold uppercase tracking-widest ${STATUS_CLASSES[r.status] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                {r.status}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] text-slate-600">
              <span>{formatDate(r.expense_date)} · {r.payment_mode}</span>
              <span className="font-display font-bold text-slate-900">{formatINR(r.actual_amount || r.estimated_amount || 0)}</span>
            </div>
            {/* Iter 140 — the AI risk score is intentionally NOT shown to
                the submitter; only geofence breaches are surfaced here
                because the user genuinely needs to know their submission
                will need a project-head override. */}
            {r.geofence_outside && (
              <div className="mt-1.5 text-[10px] flex items-center gap-1 text-rose-600">
                <MapPin size={10} /> outside geofence
              </div>
            )}
            {r.status === 'rejected' && r.reject_reason && (
              <div className="mt-1.5 text-[10px] text-rose-600 italic">Reason: {r.reject_reason}</div>
            )}
            {r.status === 'Returned for Correction' && r.return_remarks && (
              <div className="mt-1.5 text-[10px] text-amber-700 italic">Correction needed: {r.return_remarks}</div>
            )}
            {canEdit(r) && (
              <button
                onClick={() => setEditing(r)}
                className="mt-2 w-full text-[11px] font-display font-bold text-indigo-700 border border-indigo-200 rounded-sm py-1.5"
                data-testid={`m-pc-edit-${r.id}`}
              >
                Edit
              </button>
            )}
            {r.status === 'pending' && !canEdit(r) && (
              <div className="mt-1.5 text-[10px] text-slate-400 italic">Editing window closed (2 hr limit)</div>
            )}
          </div>
        ))}
      </div>
      {editing && (
        <EditExpenseSheet
          item={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
